import React, { useEffect } from 'react';
import styles from '@/styles/OverViewComponent.module.scss';
import { useAppContext } from '@/components/context';
import { getCTAS } from '@/utils';
import { PEIHeadLine, OverViewDataType, PEIImage, PEILink } from '@/common';

type OverViewComponentProps = {
  institutionID: string;
  institutionName: string;
  partnerStatus: string;
  overviewData: OverViewDataType;
  userType: string;
  name: string;
};

export const OverViewComponent = ({
  institutionName,
  institutionID,
  overviewData,
  partnerStatus,
  userType,
  name,
}: OverViewComponentProps) => {
  const { dispatch } = useAppContext();

  useEffect(() => {
    dispatch({
      type: 'setRecordInfo',
      payload: {
        key: 'overview',
        value: overviewData != undefined || JSON.stringify(overviewData) !== '{}' ? true : false,
      },
    });
  }, [dispatch, overviewData]);

  const allData = getCTAS(userType, institutionName, institutionID);
  const overViewComponentData: {
    ctaHeading?: string;
    LP: string;
    GP: string;
    'LP/GP': string;
    requestLink?: undefined | string;
    heading?: undefined | string;
    subHeading?: undefined | string;
    buttonText?: undefined | string;
  } = allData['OverViewComponent'];

  const ctas = {
    ctaButton: '',
    requestLink: '',
  };

  switch (userType) {
    case 'bronzeReachedLTL':
      ctas.requestLink = overViewComponentData.requestLink ? overViewComponentData.requestLink : '';
      break;

    case 'goldReachedLTL':
      ctas.requestLink = overViewComponentData.requestLink ? overViewComponentData.requestLink : '';
      break;
  }

  switch (partnerStatus) {
    case 'LP':
      ctas.ctaButton = overViewComponentData.LP;
      break;
    case 'GP':
      ctas.ctaButton = overViewComponentData.GP;
      break;
    case 'LP/GP':
      ctas.ctaButton = overViewComponentData['LP/GP'];
      break;
  }

  return (
    <main className={styles.container} id='overview'>
      <article className={styles['overview-container']}>
        <PEIHeadLine text={`${institutionName} overview`} />
        <main className={styles['overview-content-container']}>
          <aside>
            <BackgroundText
              background={overviewData.description.background}
              institutionName={institutionName}
              brandName={'Private Equity International'}
            />
          </aside>

          <aside className={`${styles['overview-content-details']}`}>
            <div className={styles['aum-container']}>
              <p className={styles['text-aum']}>AUM</p>
              <p className={`${styles['aum-rows']}`}>
                <PEIImage
                  src={'./account.svg'}
                  alt='lock icon image'
                  height={21}
                  width={21}
                  className={styles['overview-logos']}
                />

                <PEILink className={styles.link} href={ctas.ctaButton} target='_blank'>
                  {overViewComponentData.ctaHeading}{' '}
                  <span className={styles['keep-together']}>AUM &gt;</span>
                </PEILink>
              </p>
            </div>
            {overviewData.hQ.address.city && overviewData.hQ.address.city.name != '' && (
              <div className={styles['aum-rows']}>
                <PEIImage
                  src={'./marker.svg'}
                  alt='location market image'
                  height={21}
                  width={21}
                  className={styles['overview-logos']}
                />

                <span className={styles.text}>
                  <span className={styles['text-correction']}>
                    Headquarters: {overviewData.hQ.address.city.name},{' '}
                    {overviewData.hQ.address.country.name}
                  </span>
                </span>
              </div>
            )}
            {overviewData.website && (
              <div className={styles['aum-rows']}>
                <PEIImage
                  src={'./basketball.svg'}
                  alt='basketball image'
                  height={21}
                  width={21}
                  className={styles['overview-logos']}
                />
                <span className={styles.text}>
                  <span className={styles['text-correction']}>Website: {overviewData.website}</span>
                </span>
              </div>
            )}
            {overviewData.email && (
              <div className={styles['aum-rows']}>
                <PEIImage
                  src={'./envelope.svg'}
                  alt='email image'
                  height={21}
                  width={21}
                  className={styles['overview-logos']}
                />
                <span className={styles.text}>
                  <span className={styles['text-correction']}>Email: {overviewData.email}</span>
                </span>
              </div>
            )}
          </aside>
        </main>
      </article>
      <aside className={styles['black-box-container']}>
        <div className={styles['content-container']}>
          <PEIImage src={'./lock_white.svg'} alt='account icon' width={30.32} height={28.71} />
          <div>
            <div className={styles.heading}>{overViewComponentData.heading}</div>

            <div className={styles['sub-heading']}>
              {userType !== 'anonymous' ? (
                <>
                  {ctas.requestLink && (
                    <PEILink
                      className={styles['link-black']}
                      href={ctas.requestLink}
                      target='_blank'
                    >
                      Request a free trial.
                    </PEILink>
                  )}
                </>
              ) : (
                overViewComponentData.subHeading
              )}
            </div>
          </div>
        </div>
        <div className={styles['button-container']}>
          <PEILink className={styles.button} href={ctas.ctaButton}>
            {overViewComponentData.buttonText}
          </PEILink>
        </div>
      </aside>
    </main>
  );
};

type descriptionProps = {
  background: { __html: string } | undefined;
  institutionName: string | undefined;
  brandName: string | undefined;
};

const BackgroundText = ({ background, brandName, institutionName }: descriptionProps) => {
  if (background?.__html === 'No background data exists') {
    return (
      <>
        <p className={styles.description} data-testid='background-welcome-id'>
          Welcome to {brandName}.
        </p>
        <p
          className={styles.description}
          style={{ paddingTop: '3%' }}
          data-testid='background-text-id'
        >
          Access the latest insights, analysis and data for {institutionName} within our extensive
          information platform.
        </p>
      </>
    );
  }
  return <p className={styles.description} dangerouslySetInnerHTML={background}></p>;
};
